const HISTORY_KEY = 'rastyle_listening_history_v1';
const MAX_ITEMS = 60;
const MIN_RESUME_SECONDS = 15;
const COMPLETE_RATIO = 0.92;

const sourceTrackId = (track = {}) => String(
  track.track_id
  || track.id
  || track.youtube_id
  || track.youtubeId
  || track.video_id
  || track.url
  || track.externalUrl
  || '',
);

// Creator catalogues can legitimately expose the same provider ID (or a
// hand-entered numeric ID) for different people. Keep the legacy ID for
// ordinary platform content, but namespace creator-owned media so one
// creator's progress can never overwrite another creator's session.
const trackId = (track = {}) => {
  const sourceId = sourceTrackId(track);
  if (!sourceId) return '';
  const creatorId = String(
    track.creator_id
    || track.creatorId
    || track.creator_slug
    || track.creatorSlug
    || '',
  ).trim();
  if (!creatorId) return sourceId;
  const provider = String(track.provider || track.track_type || track.type || 'media').trim();
  return `${provider}:${creatorId}:${sourceId}`;
};

const normalizeHistoryItem = (track = {}, progress = {}) => {
  const duration = Math.max(0, Number(progress.duration ?? track.duration) || 0);
  const position = Math.max(0, Math.min(Number(progress.position) || 0, duration || Infinity));
  const ratio = duration > 0 ? position / duration : 0;
  const trackType = track.track_type
    || track.type
    || (track.youtube_id || track.youtubeId || track.video_id ? 'youtube' : 'mixtape');

  return {
    id: trackId(track),
    source_id: sourceTrackId(track),
    creator_id: String(track.creator_id || track.creatorId || ''),
    creator_slug: String(track.creator_slug || track.creatorSlug || ''),
    track_type: trackType,
    title: track.title || track.name || 'Untitled',
    artist: track.artist || track.dj || track.channel_title || '',
    cover: track.cover || track.image || track.thumbnail || track.artwork || '',
    duration,
    position: ratio >= COMPLETE_RATIO ? 0 : position,
    completed: ratio >= COMPLETE_RATIO,
    progress_ratio: ratio >= COMPLETE_RATIO ? 1 : ratio,
    hearthis_url: track.hearthis_url || track.externalUrl || '',
    resolved_audio_url: track.resolved_audio_url || track.audio_url || track.stream_url || '',
    audio_url: track.audio_url || track.stream_url || track.resolved_audio_url || '',
    stream_url: track.stream_url || track.audio_url || track.resolved_audio_url || '',
    video_url: track.video_url || (trackType === 'video' ? track.url : '') || '',
    embed_url: track.embed_url || track.embedUrl || '',
    youtube_id: track.youtube_id || track.youtubeId || track.video_id || '',
    url: track.url || track.externalUrl || '',
    provider: track.provider || '',
    last_played_at: new Date(progress.now || Date.now()).toISOString(),
    play_count: Number(progress.play_count) || 1,
  };
};

const readListeningHistory = (storage = typeof localStorage === 'undefined' ? null : localStorage) => {
  try {
    const value = JSON.parse(storage?.getItem(HISTORY_KEY) || '[]');
    return Array.isArray(value) ? value.filter((item) => item?.id) : [];
  } catch {
    return [];
  }
};

const writeListeningHistory = (items, storage = typeof localStorage === 'undefined' ? null : localStorage) => {
  try {
    storage?.setItem(HISTORY_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('rastyle:listening-history-changed'));
    return true;
  } catch {
    return false;
  }
};

const recordListeningProgress = (track, progress = {}, storage) => {
  const id = trackId(track);
  if (!id || track?.type === 'radio' || track?.track_type === 'radio') return readListeningHistory(storage);
  const current = readListeningHistory(storage);
  const previous = current.find((item) => item.id === id);
  const item = normalizeHistoryItem(track, {
    ...progress,
    play_count: previous ? previous.play_count + (progress.incrementPlayCount ? 1 : 0) : 1,
  });
  const next = [item, ...current.filter((entry) => entry.id !== id)].slice(0, MAX_ITEMS);
  writeListeningHistory(next, storage);
  return next;
};

const getContinueListening = (items = readListeningHistory(), limit = 8) => items
  .filter((item) => !item.completed && item.position >= MIN_RESUME_SECONDS && item.duration > 0)
  .sort((a, b) => new Date(b.last_played_at) - new Date(a.last_played_at))
  .slice(0, limit);

const getRecentlyPlayed = (items = readListeningHistory(), limit = 12) => [...items]
  .sort((a, b) => new Date(b.last_played_at) - new Date(a.last_played_at))
  .slice(0, limit);

const clearListeningHistory = (storage) => writeListeningHistory([], storage);

export {
  COMPLETE_RATIO,
  HISTORY_KEY,
  MAX_ITEMS,
  MIN_RESUME_SECONDS,
  clearListeningHistory,
  getContinueListening,
  getRecentlyPlayed,
  normalizeHistoryItem,
  readListeningHistory,
  recordListeningProgress,
  trackId,
  writeListeningHistory,
};
